import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ConfigureExecutionTemplateModal from 'processes/settings/ConfigureExecutionTemplateModal';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, BLUE_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import runModal from 'ui-library/runModal';
import {ellipsize} from 'util/NameUtil';

export interface EntryTemplatePreCreate {
  name: string;
  templateCollectionId: string;
  sourceTemplateId: string | null;
  publishOnSchemaCreation: boolean;
}

interface CreatedEntryTemplate {
  id: string;
  name: string;
  editUrl: string;
}

export type EntryTemplateForConfigureButton = EntryTemplatePreCreate | CreatedEntryTemplate | null;

function isCreatedEntryTemplate(
  entryTemplate: EntryTemplateForConfigureButton
): entryTemplate is CreatedEntryTemplate {
  return (entryTemplate as CreatedEntryTemplate).editUrl !== undefined;
}

export function isEntryTemplatePreCreate(
  entryTemplate: EntryTemplateForConfigureButton
): entryTemplate is EntryTemplatePreCreate {
  return (entryTemplate as EntryTemplatePreCreate).templateCollectionId !== undefined;
}

export interface OnTemplateParametersChangeArgs {
  templateCollectionId: string;
  newTemplateName: string;
  sourceTemplateId: string | null;
  publishOnSchemaCreation: boolean;
}

export interface ConfigureExecutionTemplateButtonProps {
  entryTemplate: EntryTemplateForConfigureButton;
  onChange: (args: OnTemplateParametersChangeArgs) => void;
  buttonLabel: string;
  modalTitle: string;
  defaultTemplateCollectionId?: string | undefined;
  isDisabled?: boolean | undefined;
  isHovering?: boolean | undefined;
}

export default function ConfigureExecutionTemplateButton(
  props: ConfigureExecutionTemplateButtonProps
): JSX.Element {
  const publishExecutionTemplateOnCreation =
    props.entryTemplate != null && isEntryTemplatePreCreate(props.entryTemplate)
      ? props.entryTemplate.publishOnSchemaCreation
      : null;

  const handleEditExecutionTemplate = async (): Promise<void> => {
    if (props.entryTemplate != null && !isEntryTemplatePreCreate(props.entryTemplate)) {
      assertAndContinue(false, 'Can only edit the entry template when a task schema is being created');
      return;
    }
    const returnValue = await runModal(ConfigureExecutionTemplateModal, {
      initialTemplateCollectionId:
        props.entryTemplate?.templateCollectionId ?? props.defaultTemplateCollectionId ?? null,
      templateName: props.entryTemplate?.name ?? null,
      sourceTemplateId: props.entryTemplate?.sourceTemplateId ?? null,
      modalTitle: props.modalTitle,
    });
    if (returnValue != null) {
      props.onChange({
        templateCollectionId: returnValue.templateCollection.id,
        newTemplateName: returnValue.newTemplateName,
        sourceTemplateId: returnValue.sourceTemplateId,
        publishOnSchemaCreation: publishExecutionTemplateOnCreation ?? false,
      });
    }
  };

  const entryTemplate = props.entryTemplate;
  if (entryTemplate != null) {
    const templateDisplay = (
      <div className={css(styles.innerTemplateDisplay)}>
        <BenchlingIcon icon="template" />
        <div className={css(styles.templateName)}>{ellipsize(entryTemplate.name, 56)}</div>
        {!props.isDisabled && props.isHovering && (
          <Button
            buttonStyle={ButtonStyle.LINK}
            className={css(styles.editButton)}
            onClick={async (e) => {
              e.stopPropagation();
              await handleEditExecutionTemplate();
            }}
            icon="edit"
          />
        )}
      </div>
    );
    if (isCreatedEntryTemplate(entryTemplate)) {
      return (
        <a
          className={css(styles.templateDisplay)}
          href={entryTemplate.editUrl}
          {...getTestProps({component: 'ConfigureExecutionTemplateButton', element: 'templateDisplay'})}
        >
          {templateDisplay}
        </a>
      );
    } else {
      return (
        <div>
          <span className={css(styles.templateDisplay)}>{templateDisplay}</span>
          {BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING && (
            <>
              <br />
              <CheckboxInput
                checked={publishExecutionTemplateOnCreation ?? false}
                id="template-checkbox"
                label="Publish execution template when task schema is created"
                onChange={() => {
                  props.onChange({
                    templateCollectionId: entryTemplate.templateCollectionId,
                    newTemplateName: entryTemplate.name,
                    sourceTemplateId: entryTemplate.sourceTemplateId,
                    publishOnSchemaCreation: !(publishExecutionTemplateOnCreation ?? false),
                  });
                }}
                dataTestAttrs={{
                  component: 'ConfigureExecutionTemplateButton',
                  element: 'publishTemplateCheckbox',
                }}
              />
            </>
          )}
        </div>
      );
    }
  } else {
    return (
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        disabled={props.isDisabled}
        onClick={async () => {
          await handleEditExecutionTemplate();
        }}
        size={ButtonSize.SMALL}
        fullWidth={true}
        text={props.buttonLabel}
        icon="template"
        dataTestAttrs={{component: 'ConfigureExecutionTemplateButton', element: 'button'}}
      />
    );
  }
}

const styles = StyleSheet.create({
  templateDisplay: {
    width: '100%',
    lineHeight: '16px',
  },
  templateName: {
    marginLeft: '4px',
  },
  innerTemplateDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
  },
  editButton: {
    marginLeft: 'auto',
    color: GRAY_DARK,
    boxShadow: 'none',
    paddingLeft: '8px',
    paddingRight: '8px',
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
});
